"use client";

import { useEffect, useState } from "react";
import { Menu, PhoneIncoming, Search } from "lucide-react";

import { useBookingDrawer } from "@/lib/booking-context";
import { useCallbackPanel } from "@/lib/callback-context";
import { useFaqDrawer } from "@/lib/faq-context";
import { useMenuDrawer } from "@/lib/menu-context";
import { regionFromPathname, regionPath } from "@/lib/region";
import { useSearch } from "@/lib/search-context";
import { offersFreeConsultation } from "@/lib/consultation";
import { usePublicPathname } from "@/lib/public-pathname";

export default function DesktopWidget() {
  const pathname = usePublicPathname();
  const region = regionFromPathname(pathname);
  const { setOpen: setBookingOpen } = useBookingDrawer();
  const { setOpen: setFaqOpen } = useFaqDrawer();
  const { setOpen: setMenuOpen } = useMenuDrawer();
  const { setOpen: setCallbackOpen } = useCallbackPanel();
  const { setOpen: setSearchOpen } = useSearch();
  const [visible, setVisible] = useState(false);
  const shown = visible;

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY <= 40) {
        setVisible(false);
        return;
      }
      const legal = document.querySelector("[data-footer-legal]");
      if (legal) {
        const r = legal.getBoundingClientRect();
        const vh = window.innerHeight;
        if (r.top < vh && r.bottom > vh - 100) {
          setVisible(false);
          return;
        }
      }
      setVisible(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div
      data-floating-dock
      className="pointer-events-none fixed right-8 bottom-8 z-40 hidden md:block"
      aria-hidden={!shown}
      inert={!shown}
    >
      <nav
        aria-label="Shortcuts"
        className={`bg-navy-03/75 pointer-events-auto flex items-center gap-0.5 rounded-full px-3 py-2.5 shadow-[0_20px_48px_-12px_rgba(0,0,0,0.45)] ring-1 ring-white/15 backdrop-blur-2xl backdrop-saturate-150 transition-all duration-300 ${
          shown ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
        }`}
      >
        <a
          href={regionPath("/", region)}
          aria-label="Home"
          className="mr-2.5 flex size-10 shrink-0 items-center justify-center rounded-full bg-white transition-opacity hover:opacity-90"
        >
          <span
            aria-hidden
            className="bg-teal-07 h-4 w-6 translate-x-[4.9%] translate-y-[14.8%]"
            style={{
              maskImage: "url(/assets/logo/optilase/mark-op.svg)",
              WebkitMaskImage: "url(/assets/logo/optilase/mark-op.svg)",
              maskSize: "contain",
              WebkitMaskSize: "contain",
              maskRepeat: "no-repeat",
              WebkitMaskRepeat: "no-repeat",
              maskPosition: "center",
              WebkitMaskPosition: "center",
            }}
          />
          <span className="sr-only">Home</span>
        </a>

        <div className="h-6 w-px bg-white/15" />

        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          className="flex size-10 items-center justify-center rounded-full text-white/85 transition-colors hover:bg-white/10 hover:text-white"
          aria-label="Open menu"
        >
          <Menu className="size-5" strokeWidth={1.75} />
        </button>

        <div className="h-6 w-px bg-white/15" />

        <button
          type="button"
          onClick={() => setCallbackOpen(true)}
          className="flex size-10 items-center justify-center rounded-full text-white/85 transition-colors hover:bg-white/10 hover:text-white"
          aria-label="Request a callback"
        >
          <PhoneIncoming className="size-5" strokeWidth={1.75} />
        </button>

        <div className="h-6 w-px bg-white/15" />

        <button
          type="button"
          onClick={() => setSearchOpen(true, "dock")}
          className="flex size-10 items-center justify-center rounded-full text-white/85 transition-colors hover:bg-white/10 hover:text-white"
          aria-label="Search"
        >
          <Search className="size-5" strokeWidth={1.75} />
        </button>

        <div className="h-6 w-px bg-white/15" />

        <button
          type="button"
          onClick={() => setFaqOpen(true, "desktop-dock")}
          className="flex items-center gap-1.5 rounded-full px-3 py-2.5 text-sm font-semibold text-white/85 transition-colors hover:bg-white/10 hover:text-white"
          aria-label="Open FAQs"
        >
          FAQs
        </button>

        <div className="h-6 w-px bg-white/15" />

        <button
          type="button"
          onClick={() => setBookingOpen(true, "desktop-dock")}
          className="bg-teal-07 ml-[9px] flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-bold text-white shadow-[0_4px_16px_-4px_rgba(0,133,124,0.5)] transition-transform hover:scale-105 active:scale-95"
        >
          {offersFreeConsultation(pathname) ? "Book a free consultation" : "Book consultation"}
        </button>
      </nav>
    </div>
  );
}
